import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import adminApi, { errMsg } from '../../api';
import { useAdminAuth, hasRole } from '../../auth';
import { useRange } from '../../range';
import { Band, Card } from '../../components/Card';
import DataTable from '../../components/DataTable';
import EmptyState from '../../components/EmptyState';
import KpiTile from '../../components/KpiTile';
import LineTrend from '../../components/LineTrend';
import PageHeader from '../../components/PageHeader';
import { formatCompactNumber, formatMs, formatPct, formatUsd } from '../../format';
import useSort, { applySort } from '../../hooks/useSort';

/**
 * AI › Stylist — usage, quality, speed, safety and cost for MAHI herself.
 *
 * Renamed from "MAHI", which as a nav label inside MAHI's own admin panel
 * carried no information — everything here is MAHI.
 *
 * Everything is segmented by persona version, which is the whole point: it
 * is how a prompt change gets traced to its actual effect on real
 * conversations instead of argued about. A change that makes her ask one
 * more clarifying question shows up as fewer zero-results, more turns per
 * conversation, and a higher cost per conversation — three numbers moving
 * together in a direction someone predicted.
 */
const RAMP = { p50: '#86b6ef', p90: '#2a78d6', p99: '#104281' };
// Three distinct quality signals, not one measure at three depths — so these
// are categorical identities rather than a ramp.
const SERIES = { zero: '#2a78d6', safety: '#eb6834', validation: '#1baf7a' };

export default function StylistPage() {
  const { admin } = useAdminAuth();
  const range = useRange();
  const canReadTranscripts = hasRole(admin, 'owner');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get('/mahi/overview', { params: { days: range.days } });
      setData(data);
    } catch (err) {
      toast.error(errMsg(err, 'Could not load stylist metrics'));
    } finally {
      setLoading(false);
    }
  }, [range.days]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};
  const trend = data?.trend || [];
  const noData = data?.status === 'no_data';

  const percentileSeries = (prefix) => ['p50', 'p90', 'p99'].map((p) => ({
    key: p, label: p, color: RAMP[p],
    values: trend.map((d) => ({ x: d.date, y: d[prefix ? `${prefix}_${p}_ms` : `${p}_ms`] })),
  }));

  const qualitySeries = [
    { key: 'zero', label: 'Zero results', color: SERIES.zero, values: trend.map((d) => ({ x: d.date, y: d.zero_result_pct })) },
    { key: 'safety', label: 'Safety blocks', color: SERIES.safety, values: trend.map((d) => ({ x: d.date, y: d.safety_block_pct })) },
    { key: 'validation', label: 'Grounding blocks', color: SERIES.validation, values: trend.map((d) => ({ x: d.date, y: d.validation_block_pct })) },
  ];

  const personaColumns = [
    { key: 'persona_version', label: 'Persona', render: (r) => <span className="font-mono font-medium text-ink">{r.persona_version}</span> },
    {
      key: 'live',
      label: 'Live',
      render: (r) => (r.first_seen === r.last_seen ? r.first_seen : `${r.first_seen} → ${r.last_seen}`),
    },
    { key: 'turns', label: 'Turns', align: 'right', sortable: true, render: (r) => formatCompactNumber(r.turns) },
    { key: 'conversations', label: 'Convs', align: 'right', sortable: true, render: (r) => formatCompactNumber(r.conversations) },
    { key: 'turns_per_conversation', label: 'Turns/conv', align: 'right', sortable: true, render: (r) => r.turns_per_conversation },
    { key: 'zero_result_pct', label: 'Zero results', align: 'right', sortable: true, render: (r) => formatPct(r.zero_result_pct, 1) },
    { key: 'safety_block_pct', label: 'Safety', align: 'right', sortable: true, render: (r) => formatPct(r.safety_block_pct, 1) },
    { key: 'validation_block_pct', label: 'Grounding', align: 'right', sortable: true, render: (r) => formatPct(r.validation_block_pct, 1) },
    { key: 'first_token_p90_ms', label: 'First token p90', align: 'right', sortable: true, render: (r) => formatMs(r.first_token_p90_ms) },
    { key: 'p90_ms', label: 'Turn p90', align: 'right', sortable: true, render: (r) => formatMs(r.p90_ms) },
    {
      key: 'cost_per_conversation_usd',
      label: 'Cost/conv',
      align: 'right',
      sortable: true,
      render: (r) => (data?.pricing_configured ? formatUsd(r.cost_per_conversation_usd) : null),
    },
  ];

  const { sort, onSort } = useSort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sortedPersonas = useMemo(() => applySort(data?.by_persona || [], sort, personaColumns), [data, sort]);

  return (
    <div>
      <PageHeader
        title="Stylist"
        hint={`How MAHI is performing — ${range.label.toLowerCase()}. Segmented by persona version, so a prompt change can be traced to its effect.`}
      />

      {loading && !data && <Card><p className="text-sm text-ink-muted">Loading…</p></Card>}

      {noData && (
        <EmptyState
          tone="gap"
          title="No stylist rollup for this window"
          detail="These numbers come from the nightly rollup of chat.turn_completed, chat.first_token, chat.zero_results, guardrail.blocked and rationale.shown. Nothing has landed for these dates."
        />
      )}

      {data && !noData && (
        <>
          <Band title="Usage">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <KpiTile label="Turns" value={formatCompactNumber(totals.turns)} />
              <KpiTile label="Conversations" value={formatCompactNumber(totals.conversations)} />
              <KpiTile
                label="Turns per conversation"
                value={totals.turns_per_conversation ?? '-'}
                caption="A rise can mean better clarifying — or her failing to land."
              />
              <KpiTile
                label="Peak daily users"
                value={formatCompactNumber(totals.peak_daily_users)}
                caption={`${formatCompactNumber(totals.user_days)} user-days in window`}
              />
            </div>
          </Band>

          <Band
            title="Quality & safety, as a share of turns"
            actions={<span className="text-[11px] text-ink-muted">Zero results counts only turns where a search was available.</span>}
          >
            <LineTrend series={qualitySeries} unit="%" format={(v) => Math.round(v)} height={180} />
            <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <KpiTile label="Zero-result rate" value={formatPct(totals.zero_result_pct, 1)} goodWhen="down" caption="Of searchable turns" />
              <KpiTile label="Safety blocks" value={formatPct(totals.safety_block_pct, 1)} goodWhen="down" caption="Stopped before the model" />
              <KpiTile label="Grounding blocks" value={formatPct(totals.validation_block_pct, 1)} goodWhen="down" caption="Scrubbed after the model" />
              <KpiTile label="Rationale specificity" value={formatPct(totals.rationale_specific_pct, 1)} caption="Cards that got a written line" />
            </div>
          </Band>

          <Band title="Speed">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div>
                <h3 className="mb-2 text-xs font-medium text-ink-secondary">
                  Time to first token — what she feels like to talk to
                </h3>
                <LineTrend series={percentileSeries('first_token')} format={(v) => (v / 1000).toFixed(1)} unit="s" height={160} />
              </div>
              <div>
                <h3 className="mb-2 text-xs font-medium text-ink-secondary">
                  Full turn — including tool rounds
                </h3>
                <LineTrend series={percentileSeries(null)} format={(v) => (v / 1000).toFixed(1)} unit="s" height={160} />
              </div>
            </div>
          </Band>

          <Band title="Cost">
            {data.pricing_configured ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <KpiTile
                  label="Spend"
                  value={formatUsd(totals.cost_usd)}
                  caption={`Priced on ${totals.days_priced} of ${totals.days_with_data} days with data`}
                />
                <KpiTile label="Per conversation" value={formatUsd(totals.cost_per_conversation_usd)} goodWhen="down" />
                <KpiTile label="Per turn" value={formatUsd(totals.cost_per_turn_usd)} goodWhen="down" />
                <KpiTile
                  label="Cached input tokens"
                  value={formatCompactNumber(totals.cache_read_tokens)}
                  caption="Billed at the cheaper cache-read rate"
                />
              </div>
            ) : (
              <Card>
                <p className="text-sm font-medium text-ink">No model pricing configured</p>
                <p className="mt-1 text-sm leading-relaxed text-ink-secondary">
                  Token counts are being recorded, but no per-million rates are set, so no cost is shown.
                  Set <code className="text-xs">MAHI_MODEL_PRICING</code> on the MAHI backend to turn this on.
                  A zero here would be a made-up number, so there isn&apos;t one.
                </p>
                <p className="mt-2 text-xs text-ink-muted">
                  Tokens so far: {formatCompactNumber(totals.input_tokens)} in ·{' '}
                  {formatCompactNumber(totals.output_tokens)} out ·{' '}
                  {formatCompactNumber(totals.cache_read_tokens)} from cache
                </p>
              </Card>
            )}
          </Band>

          <Band
            title="By persona version"
            actions={<span className="text-[11px] text-ink-muted">Each row covers only the days that persona was live.</span>}
          >
            <DataTable
              columns={personaColumns}
              rows={sortedPersonas}
              rowKey={(r) => r.persona_version}
              dense
              sort={sort}
              onSort={onSort}
              empty={<EmptyState title="No persona versions recorded in this window" />}
            />
          </Band>

          {canReadTranscripts && (
            <Card title="Conversation review">
              <p className="mb-3 text-xs leading-relaxed text-ink-secondary">
                Reading an actual transcript is owner-only and every view is written to the audit log,
                naming the conversation. It is the most sensitive data in this panel — the numbers above
                are the right tool for almost every question.
              </p>
              <Link
                to="/ai/transcripts"
                className="inline-block rounded-lg border border-hairline px-3 py-1.5 text-xs font-medium text-ink hover:bg-surface-page"
              >
                Open transcripts
              </Link>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
